import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "./ui/button";

const money = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const build = (items, year) => {
  const by = {};
  items.filter((i) => String(i.start || "").startsWith(year)).forEach((i) => {
    const r = (by[i.employee_id] ||= { id: i.employee_id, name: i.employee_name, total: 0, qty: 0 });
    r.total += Number(i.total) || 0; r.qty += Number(i.qty) || 1;
  });
  const rows = Object.values(by).sort((a, b) => b.total - a.total);
  const avg = rows.length ? rows.reduce((a, r) => a + r.total, 0) / rows.length : 0;
  rows.forEach((r) => { r.diff = avg ? Math.round(((r.total - avg) / avg) * 100) : 0; });
  return { rows, avg, sum: rows.reduce((a, r) => a + r.total, 0) };
};

const exportXlsx = async (rows, year, avg) => {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(`تكلفة المعدات ${year}`, { views: [{ rightToLeft: true }] });
  ws.columns = [{ header: "المندوب", width: 32 }, { header: "عدد القطع", width: 12 }, { header: "التكلفة (ر.س)", width: 16 }, { header: "مقارنة بالمتوسط %", width: 18 }];
  rows.forEach((r) => { const row = ws.addRow([r.name, r.qty, Math.round(r.total * 100) / 100, r.diff]); if (r.diff >= 50) row.font = { color: { argb: "FFDC2626" }, bold: true }; });
  ws.addRow([]); ws.addRow(["متوسط المندوب", "", Math.round(avg * 100) / 100, ""]);
  const buf = await wb.xlsx.writeBuffer();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([buf])); a.download = `تكلفة_المعدات_${year}.xlsx`; a.click();
};

export const EquipmentCost = ({ items }) => {
  const years = [...new Set([String(new Date().getFullYear()), ...items.map((i) => String(i.start || "").slice(0, 4)).filter(Boolean)])].sort().reverse();
  const [year, setYear] = useState(years[0]);
  const { rows, avg, sum } = build(items, year);
  const max = rows[0]?.total || 1;
  return (
    <div className="space-y-3" data-testid="ev-cost">
      <div className="flex flex-wrap items-center gap-2">
        <select value={year} onChange={(e) => setYear(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold" data-testid="ev-cost-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-slate-500" data-testid="ev-cost-summary">الإجمالي: <b className="text-slate-800">{money(sum)}</b> ر.س · متوسط المندوب: <b className="text-slate-800">{money(avg)}</b> ر.س · {rows.length} مندوب</span>
        {rows.length > 0 && <Button size="sm" variant="outline" className="ms-auto gap-1.5" onClick={() => exportXlsx(rows, year, avg)} data-testid="ev-cost-export"><Download className="h-4 w-4" /> تصدير Excel</Button>}
      </div>
      {rows.length === 0 ? <p className="py-10 text-center text-sm text-slate-400" data-testid="ev-cost-empty">لا توجد معدات في {year}</p> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {rows.map((r, i) => {
            const hi = r.diff >= 50;
            return (
              <div key={r.id} className={`grid grid-cols-[28px_1fr_auto] items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-0 ${hi ? "bg-red-50" : ""}`} data-testid={`ev-cost-row-${r.id}`}>
                <span className="text-center text-xs font-bold text-slate-400">{i + 1}</span>
                <div className="min-w-0">
                  <p className={`truncate text-sm font-bold ${hi ? "text-red-700" : "text-slate-800"}`}>{r.name}</p>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${hi ? "bg-red-500" : "bg-indigo-500"}`} style={{ width: `${(r.total / max) * 100}%` }} /></div>
                </div>
                <div className="text-end">
                  <p className="text-sm font-black text-slate-800">{money(r.total)} <span className="text-[10px] font-normal text-slate-400">ر.س · {r.qty} قطعة</span></p>
                  <p className={`text-[11px] font-bold ${r.diff >= 50 ? "text-red-600" : r.diff > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid={`ev-cost-diff-${r.id}`}>{r.diff > 0 ? `أعلى من المتوسط ${r.diff}%` : r.diff < 0 ? `أقل من المتوسط ${-r.diff}%` : "مساوٍ للمتوسط"}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
